import { fetchSavedDocument } from '../services/i18nService';
import { getActiveLocale } from './translate';
import { getFallbackChain } from './languages';

/**
 * Long-form documents translated as whole files in Translation Admin.
 *
 * English lives at /content/<file>. A built-in AI draft for a language lives at
 * /content/<code>/<file> and must be listed in builtInDrafts (the dev server
 * answers missing files with index.html, so we never probe for drafts).
 * A version saved in Translation Admin wins over the built-in draft.
 */
export interface TranslatableDocument {
  id: string;
  file: string;
  title: string;
  group: 'site' | 'guidance';
  builtInDrafts: string[];
}

const guidance = (n: number): TranslatableDocument => ({
  id: `step_${n}_guidance`,
  file: `Step_${n}_Guidance.md`,
  title: `Step ${n} guidance`,
  group: 'guidance',
  builtInDrafts: ['es'],
});

export const TRANSLATABLE_DOCUMENTS: TranslatableDocument[] = [
  { id: 'privacy', file: 'Recovery-Commons-Privacy.md', title: 'Privacy', group: 'site', builtInDrafts: ['es'] },
  { id: 'about', file: 'Recovery-Commons-About.md', title: 'About Recovery Commons', group: 'site', builtInDrafts: ['es'] },
  ...Array.from({ length: 12 }, (_, i) => guidance(i + 1)),
  {
    id: 'character_defects_step6',
    file: 'character_defects_step6.md',
    title: 'Character defects (Step 6)',
    group: 'guidance',
    builtInDrafts: ['es'],
  },
];

export const DOCUMENT_GROUP_LABELS: Record<TranslatableDocument['group'], string> = {
  site: 'Site pages',
  guidance: 'Step guidance',
};

export function getDocument(id: string): TranslatableDocument | undefined {
  return TRANSLATABLE_DOCUMENTS.find((d) => d.id === id);
}

export function englishPath(doc: TranslatableDocument): string {
  return `/content/${doc.file}`;
}

export function builtInDraftPath(doc: TranslatableDocument, code: string): string {
  return `/content/${code}/${doc.file}`;
}

function documentForPath(path: string): TranslatableDocument | undefined {
  const clean = path.split(/[?#]/)[0];
  return TRANSLATABLE_DOCUMENTS.find((d) => englishPath(d) === clean);
}

async function fetchText(path: string): Promise<string | null> {
  try {
    const res = await fetch(path);
    if (!res.ok) return null;
    const text = await res.text();
    if (/^\s*<!doctype html/i.test(text)) return null;
    return text;
  } catch {
    return null;
  }
}

/** Built-in AI draft for a language, or null when none ships with the app. */
export async function fetchBuiltInDraft(doc: TranslatableDocument, code: string): Promise<string | null> {
  if (!doc.builtInDrafts.includes(code)) return null;
  return fetchText(builtInDraftPath(doc, code));
}

const CACHE_MS = 60_000;
const contentCache = new Map<string, { at: number; text: string }>();

export function clearLocalizedContentCache(): void {
  contentCache.clear();
}

async function resolveLocalizedContent(path: string, locale: string): Promise<string> {
  const doc = documentForPath(path);
  if (doc) {
    for (const code of getFallbackChain(locale)) {
      if (code === 'en') break;
      const text = (await fetchSavedDocument(code, doc.id)) ?? (await fetchBuiltInDraft(doc, code));
      if (text) return text;
    }
  }
  const res = await fetch(path);
  if (!res.ok) throw new Error(`Failed to load content: ${res.statusText}`);
  return res.text();
}

/**
 * Load a content file in the reader's language: for each language in the
 * fallback chain, a version saved in Translation Admin, else the built-in
 * draft; finally English. Files that aren't translatable load as-is.
 */
export async function loadLocalizedContent(path: string, locale: string = getActiveLocale()): Promise<string> {
  const cacheKey = `${locale}|${path}`;
  const hit = contentCache.get(cacheKey);
  if (hit && Date.now() - hit.at < CACHE_MS) return hit.text;
  const text = await resolveLocalizedContent(path, locale);
  contentCache.set(cacheKey, { at: Date.now(), text });
  return text;
}
